import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'ramda';
import { useLocation, useNavigate } from 'react-router-dom';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import axios from 'axios';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';

import {
  Wrapper,
  Form,
  OptionsWrapper,
  CommentsWrapper,
  Label,
  Title,
  ThanksText,
  TextArea,
  Submit,
  RichText
} from './StyledPageFeedback';

import Radio from '_utils/components/Corporate/Radio';
import PropTypes from 'prop-types';

export const PageFeedback = (props) => {
  const [t] = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [helpful, setHelpful] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [bowserLoaded, setBowserLoaded] = useState(null);
  const Bowser = useRef<any>();

  const { sitecoreContext, editMode, rendering } = props;
  const showPageFeedback = sitecoreContext?.route?.fields?.onPageFeedback.value || null;
  const sendFeedbackParam = Number(rendering?.params?.sendFeedback);

  const endpoint =
    'https://script.google.com/macros/s/AKfycbysjcflZyONKGhwLjHf6TFIawugXvA2MOvNjAzzfKp0-BuLOh-c9i6R59ybbwPcLeBuwQ/exec';

  useEffect(() => {
    // optimise initial load by deferring this lib
    async function getBowser() {
      const { default: Bowser } = await import(/* webpackChunkName: "bowser" */ 'bowser');
      return Bowser;
    }

    getBowser().then((bowser) => {
      Bowser.current = bowser;
      setBowserLoaded(true);
    });
  }, []);

  // reset form on page transition
  useEffect(() => {
    setHelpful(null);
    setComment('');
    setLoading(false);
    setResponse(null);
    setError(null);
  }, [pathname, search]);

  if (editMode || !showPageFeedback) {
    return null;
  }

  const onHelpfulChange = (event) => {
    setHelpful(event.target.value);
  };

  const onCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    setLoading(true);

    let path = '';
    path = window.location.href;
    const browser = Bowser.current.getParser(window.navigator.userAgent);
    let getBrowser = browser.getBrowser();

    let data = {
      wasHelpful: helpful,
      comment,
      url: path,
      domain: 'www.cityofsydney.nsw.gov.au',
      browser: `${getBrowser.name} ${getBrowser.version}`,
      customText: t('page-feedback-question-text')
    };

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };

    axios
      .post(endpoint, queryString.stringify(data), {
        headers: headers
      })
      .then((response) => {
        if (response && response.data) {
          setResponse(response.data);
        }
      })
      .catch((err) => {
        if (err) {
          console.error(err);

          setError(err);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  if (!bowserLoaded) {
    return null;
  }

  const showLinkToFeedbackForm = helpful === null && sendFeedbackParam !== 1;
  const showRadioButtonFeedback = helpful === null;
  const showTextAreaFeedback = helpful === helpful !== undefined && helpful !== null && response === null;

  return (
    <Wrapper $sendFeedback={showLinkToFeedbackForm}>
      <Form onSubmit={handleFormSubmit}>
        {showLinkToFeedbackForm ? (
          // Show link to feedback form
          <RichText navigate={navigate} field={{ value: `${t('page-feedback-contact-us-text')}` }} />
        ) : (
          // Collect feedback on this page
          <>
            {showRadioButtonFeedback && (
              <OptionsWrapper>
                <Title>{t('page-feedback-question-text')}</Title>
                <div>
                  <Radio
                    id="wasHelpfulYes"
                    name="wasHelpful"
                    value="yes"
                    onChange={onHelpfulChange}
                    checked={helpful === 'yes'}
                  >
                    <Label htmlFor="wasHelpfulYes">{t('page-feedback-yes-label')}</Label>
                  </Radio>
                </div>
                <div>
                  <Radio
                    id="wasHelpfulNo"
                    name="wasNoHelpful"
                    value="no"
                    onChange={onHelpfulChange}
                    checked={helpful === 'no'}
                  >
                    <Label htmlFor="wasHelpfulNo">{t('page-feedback-no-label')}</Label>
                  </Radio>
                </div>
              </OptionsWrapper>
            )}

            {showTextAreaFeedback && (
              <CommentsWrapper>
                <TextArea
                  placeholder={t('page-feedback-comments-placeholder-text')}
                  value={comment}
                  name="comment"
                  onChange={onCommentChange}
                  disabled={loading}
                ></TextArea>
                <Submit type="submit">{loading ? 'Sending' : 'Send'}</Submit>
              </CommentsWrapper>
            )}
            {response && <ThanksText>Thanks for your feedback!</ThanksText>}
            {error && <ThanksText>Failed to send feedback</ThanksText>}
          </>
        )}
      </Form>
    </Wrapper>
  );
};

PageFeedback.propTypes = {
  sitecoreContext: PropTypes.shape({
    route: PropTypes.shape({
      fields: PropTypes.shape({
        onPageFeedback: PropTypes.shape({
          value: PropTypes.bool
        })
      })
    })
  }),
  editMode: PropTypes.bool,
  rendering: PropTypes.shape({
    params: PropTypes.shape({
      sendFeedback: PropTypes.string
    })
  })
};

export default compose(withSitecoreContext())(PageFeedback);
